'use client';
import type { FlexibleFormVariant } from './FlexibleFormBase';
import { isLicensePlateFormAsset } from './flexibleFormShared';
import { isPromoCodeFormField } from './promoCodeEntryValidation';
import { createPromoCodeCustomSubmit } from './promoCodeFlexibleFormSubmit';
import { createRflzCustomSubmit, getRequiredRflzFieldNames } from './rflzFlexibleFormSubmit';
import { createLicensePlateCustomSubmit } from './licensePlateFlexibleFormSubmit';
import { createFlepzCustomSubmit, getRequiredFlepzFieldNames } from './flepzFlexibleFormSubmit';
import { createEmailHandoffCustomSubmit } from './emailHandoffFlexibleFormSubmit';
import { EMAIL_HANDOFF_FORM_TYPE } from './emailHandoffConfig';

interface FlexibleFormCustomSubmitArgs {
    asset: SiteFlexibleForm;
    forceRflzSubmit?: boolean;
    emailHandoffUrl?: string;
}

export function createFlexibleFormCustomSubmit({
    asset,
    forceRflzSubmit = false,
    emailHandoffUrl,
}: FlexibleFormCustomSubmitArgs): FlexibleFormVariant['customSubmit'] {
    const formType = asset.fields.type.toLowerCase();

    if (formType === EMAIL_HANDOFF_FORM_TYPE) {
        const emailFields = asset.fields.form_fields.filter(
            (field) => field.fields.type.toLowerCase() === 'email only' && field.fields.required,
        );
        return createEmailHandoffCustomSubmit({
            emailFieldName: emailFields.length === 1 ? emailFields[0].fields.field_name : undefined,
            redirectUrl: emailHandoffUrl,
        });
    }

    if (formType === 'promocodeentry') {
        const promoCodeFieldName = asset.fields.form_fields.find(isPromoCodeFormField)?.fields.field_name;
        return createPromoCodeCustomSubmit(promoCodeFieldName, {
            successPageRedirectHref: asset.fields.success_page_redirect?.fields.href,
            invalidCodeMessage: asset.fields.error_subheadline,
        });
    }

    if (formType === 'widgetform-flepz') {
        return createFlepzCustomSubmit(getRequiredFlepzFieldNames(asset.fields.form_fields));
    }

    const shouldUseRflzSubmit = forceRflzSubmit || formType === 'rflz';

    if (!shouldUseRflzSubmit) {
        return undefined;
    }

    if (isLicensePlateFormAsset(asset)) {
        return createLicensePlateCustomSubmit(asset.fields.form_fields);
    }

    return createRflzCustomSubmit(getRequiredRflzFieldNames(asset.fields.form_fields));
}
